import { Fragment, forwardRef } from 'react';

import { TableCell, TableCellProps, Typography } from '@material-ui/core';

import {
  ReanalysisDataExposureState,
  ReanalysisDataMetricDataState,
  ReanalysisListItemFragment,
} from '@spotify/confidence-graphql';

const formatNumber = (number: number) => {
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(number);
};

export const Bullet = () => <Fragment> &bull; </Fragment>;

interface ProgressCellProps extends TableCellProps {
  workflowInstance: Pick<
    ReanalysisListItemFragment,
    'checks' | 'moduleData2' | 'state'
  >;
}

export const ProgressCell = forwardRef<HTMLTableCellElement, ProgressCellProps>(
  ({ workflowInstance, ...props }, ref) => {
    const exposure = workflowInstance.moduleData2?.exposure?.exposure?.reduce(
      (acc, curr) => acc + (Number(curr.totalExposureCount) || 0),
      0,
    );

    const isCalculatingExposure =
      workflowInstance.moduleData2?.reanalysis?.exposureState ===
      ReanalysisDataExposureState.StateRunning;

    const isCalculatingMetrics =
      workflowInstance.moduleData2?.reanalysis?.metrics?.some(
        m => m.state === ReanalysisDataMetricDataState.StateRunning,
      );

    const isCalculating = isCalculatingExposure || isCalculatingMetrics;

    const isDone =
      workflowInstance.moduleData2?.reanalysis?.exposureState ===
        ReanalysisDataExposureState.StateCompleted &&
      workflowInstance.moduleData2?.reanalysis?.metrics?.every(
        m => m.state === ReanalysisDataMetricDataState.StateCompleted,
      );

    const isDraft =
      workflowInstance.moduleData2?.reanalysis?.exposureState ===
        ReanalysisDataExposureState.StateUnspecified ||
      !workflowInstance.moduleData2?.reanalysis?.exposureState;

    const getProgress = () => {
      if (isCalculating) return 'Calculating';
      if (isDone) return 'Done';
      if (isDraft) return 'Draft';
      return 'Error';
    };

    const progress = getProgress();

    return (
      <TableCell {...props} ref={ref}>
        {progress}
        {exposure !== undefined && (
          <Typography variant="caption" color="textSecondary" component="div">
            {exposure === 0
              ? 'No exposure'
              : `${formatNumber(exposure)} exposed`}
          </Typography>
        )}
      </TableCell>
    );
  },
);
ProgressCell.displayName = 'ProgressCell';
